import {
  Day,
  DragAndDrop,
  type DragEventArgs,
  Inject,
  type RenderCellEventArgs,
  Resize,
  ResourceDirective,
  ResourcesDirective,
  ScheduleComponent,
  ViewDirective,
  ViewsDirective,
} from '@syncfusion/ej2-react-schedule'
import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import './scheduleCalendar.scss'

import sendMessageIcon from 'assets/icons/send-message.svg'
import { Button } from 'common/components/Button/Button'
import { Show } from 'common/components/Show/Show'
import { ALT_CONSTANTS } from 'common/constants/altConstants'
import { BUTTON_CONSTANTS, BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import type { ObjectType } from 'common/interfaces/object'
import { DateService } from 'common/services/dateService'
import dayjs from 'dayjs'
import { AnnounceIconButton } from 'features/Home/Book/Booking/Schedule/Announce/announce'
import { AnnounceModal } from 'features/Home/Book/Booking/Schedule/Announce/announce-modal'
import { SendSMSConfirmation } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/SendSMSConfirmation/SendSMSConfirmation'
import { Warning } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/Warning/Warning'
import { PatientScheduleSkeleton } from 'features/Home/Book/Booking/Schedule/PatientSchedule/PatientScheduleSkeleton/PatientScheduleSkeleton'
import { INFO_CONSTANTS, MIN_PRE_POPULATE_TIME } from 'features/Home/constants/infoConstants'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { useFetchEmrLinkedProvidersQuery } from 'features/Patients/state/api/patientApi'
import { getEmrLinkedResidenUserIds } from 'features/Patients/state/api/emrProviders.transform'
import { useParams } from 'react-router'
import { useToggle } from 'react-use'
import { useScheduleHeight } from '../useScheduleHeight'
import { resolveDragGroupIndex, resolveScheduleRoomId } from './helpers'
import {
  availabilityClassName,
  availabilityLabelForCell,
  buildDisplayBlocks,
  statusForCell,
} from './scheduleAvailability'
import type { ScheduleCalendarProps } from './scheduleCalendar.types'
import { scheduleEventStore } from './scheduleEventStore'
import { useFrozenValue } from './useFrozenValue'
import { useInteractionFreeze } from './useInteractionFreeze'
import { useScheduleCalendar } from './useScheduleCalendar'
import { useScheduleCalendarReady } from './useScheduleCalendarReady'
import { useScheduleColumnWidths } from './useScheduleColumnWidths'
import { useScheduleView } from './useScheduleView'
import { useStableResourceDataSource } from './useStableResourceDataSource'
import { useStableScheduleDataSource } from './useStableScheduleDataSource'

export const ScheduleCalendar = memo(
  ({
    createCustomAppointment,
    setEditedPatient,
    handleUpdateEvents,
    sendInfoPatientsNotification = async () => {},
    prepopulateTimeOnClick = MIN_PRE_POPULATE_TIME,
    showConsultationStaffInitials = false,
    resources = [],
    dayInterval,
    interval,
    disabledIntervals,
    timeZone,
    rooms,
    schedules,
    isSendingNotifications,
    eventsToSendNotification,
    patientStatuses,
    appointmentCategories,
    appointmentColor,
    hasEhrBooking,
    instanceKey,
    resourceGrouping = 'rooms',
    stacked = false,
    showAnnounceButton = true,
    showNotifyPatients = true,
    focusedStaffIds,
    scaleColumnsByOverlap = true,
    availability = [],
    availabilityStaffIds = [],
  }: ScheduleCalendarProps) => {
    const { day } = useParams()
    const { data: emrProviders, isSuccess: emrProvidersLoaded } = useFetchEmrLinkedProvidersQuery()
    const emrLinkedUserIds = useMemo(
      () => (emrProvidersLoaded ? getEmrLinkedResidenUserIds(emrProviders ?? []) : new Set<number>()),
      [emrProviders, emrProvidersLoaded],
    )
    const [showBusyNotification, setShowBusyNotification] = useToggle(false)
    const [isSendSmsConfirmVisible, setIsSendSmsConfirmVisible] = useToggle(false)
    const [openAnnounceModal, toggleAnnounceModal] = useToggle(false)

    const scheduleObj = useRef<ScheduleComponent>(null)
    const scheduleContainerRef = useRef<HTMLDivElement>(null)
    const skipNextEventChangeRef = useRef(false)

    const formattedDate = useMemo(() => {
      if (!timeZone || !day) {
        return null
      }

      return dayjs(day).tz(timeZone, true).format('YYYY-MM-DD')
    }, [timeZone, day])

    const {
      events,
      pendingCreation,
      cancelCreation,
      confirmCreation,
      awaitConfirmationEvent,
      prepareScheduleChange,
      commitScheduleChange,
      handleScheduleChange,
      rejectUnavailableRange,
      setAwaitConfirmationEvent,
      proceedToUpdateEvents,
      handleSelect,
    } = useScheduleCalendar({
      schedules,
      resources,
      dayInterval,
      disabledIntervals,
      formattedDate,
      timeZone,
      rooms,
      scheduleObj,
      prepopulateTimeOnClick,
      createCustomAppointment,
      handleUpdateEvents,
      instanceKey,
      resourceGrouping,
      availability,
      availabilityStaffIds,
      emrLinkedUserIds,
    })

    const { isInteracting, handleInteractionStart, handleInteractionEnd } = useInteractionFreeze()
    const { isReady, markReady } = useScheduleCalendarReady(instanceKey ?? day)

    const data = useFrozenValue(useStableScheduleDataSource(events), isInteracting)
    const resourceDataSource = useFrozenValue(useStableResourceDataSource(resources), isInteracting)

    const columnKeys = useMemo(
      () => resourceDataSource.map((resource) => String(resource.resourceId)),
      [resourceDataSource],
    )

    const availabilityBlocksByResource = useMemo(() => {
      const blocks = new Map<string, ReturnType<typeof buildDisplayBlocks>>()
      resources.forEach((resource) => {
        if (resourceGrouping === 'staff') {
          const staffId = Number(resource.resourceId)
          if (!Number.isFinite(staffId) || staffId <= 0 || !emrLinkedUserIds.has(staffId)) return
          blocks.set(String(resource.resourceId), buildDisplayBlocks(availability, [staffId]))
          return
        }

        const room = rooms.find(
          (room) =>
            `${room.label}/${room.value}` === (resource.roomResourceId ?? resource.resourceId),
        )
        if (resourceGrouping !== 'rooms' || !room?.is_waiting_room || resource.staffId === 0) return
        const staffIds = (resource.staffId != null ? [resource.staffId] : availabilityStaffIds).filter(
          (staffId) => emrLinkedUserIds.has(staffId),
        )
        if (!staffIds.length) return
        blocks.set(String(resource.resourceId), buildDisplayBlocks(availability, staffIds))
      })
      return blocks
    }, [availability, availabilityStaffIds, emrLinkedUserIds, resources, rooms, resourceGrouping])

    const flexColumnKey = useMemo(() => {
      if (resourceGrouping === 'staff') return undefined
      const waitingRoom = rooms.find((room) => room.is_waiting_room)
      if (!waitingRoom) return undefined
      const roomResourceId = `${waitingRoom.label}/${waitingRoom.value}`
      return (
        resources.find((resource) => resource.roomResourceId === roomResourceId)?.resourceId ??
        roomResourceId
      )
    }, [rooms, resources, resourceGrouping])

    const { referenceWidthByColumn, setColumnReferenceWidth } = useScheduleColumnWidths(
      resourceGrouping === 'staff' ? 'day-staff' : 'day',
    )

    const {
      eventRendered,
      eventTemplate,
      handleEventClick,
      handleChange,
      handleDataBound,
      handleActionFailure,
      handlePopupOpen,
      handleRemoveSelectedCells,
      reapplyAppointmentColors,
      applyDynamicColumnWidths,
      selectedDate,
      timeScale,
      workHours,
      eventSettings,
    } = useScheduleView({
      data,
      schedules,
      scheduleObj,
      interval,
      dayInterval,
      day,
      timeZone,
      appointmentColor,
      appointmentCategories,
      patientStatuses,
      hasEhrBooking,
      showConsultationStaffInitials,
      columnKeys,
      flexColumnKey,
      referenceWidthByColumn,
      setColumnReferenceWidth,
      setEditedPatient,
      prepareScheduleChange,
      commitScheduleChange,
      skipNextEventChangeRef,
      instanceKey,
      focusedStaffIds,
      scaleColumnsByOverlap,
    })

    const handleDragStop = useCallback(
      (args: DragEventArgs) => {
        handleInteractionEnd()
        requestAnimationFrame(() => scheduleObj.current?.removeSelectedClass())

        const eventData = args.data as unknown as IPatientSchedule | undefined
        const stored =
          eventData?.id != null
            ? (scheduleEventStore.get(Number(eventData.id), instanceKey) as
                | IPatientSchedule
                | undefined)
            : undefined
        const originalRoomId = resolveScheduleRoomId(stored?.room_id ?? eventData?.room_id)
        const originalGroupIndex = resourceDataSource.findIndex(
          (resource) => resource.resourceId === originalRoomId,
        )

        const groupIndex = resolveDragGroupIndex(
          args,
          scheduleObj.current,
          originalGroupIndex >= 0 ? originalGroupIndex : null,
        )
        const dropResourceId =
          groupIndex == null ? undefined : resourceDataSource[groupIndex]?.resourceId
        const nextRoomId = dropResourceId || resolveScheduleRoomId(eventData?.room_id)

        if (eventData && nextRoomId) {
          eventData.room_id = nextRoomId
        }

        if (eventData?.start_time && eventData?.end_time) {
          const dropStart = new Date(eventData.start_time)
          const dropEnd = new Date(eventData.end_time)
          if (!Number.isNaN(dropStart.getTime()) && rejectUnavailableRange(dropStart, dropEnd)) {
            args.cancel = true
            skipNextEventChangeRef.current = true
            return
          }
        }

        if (eventData?.id && nextRoomId) {
          skipNextEventChangeRef.current = true
          handleScheduleChange({ ...eventData, room_id: nextRoomId }).finally(() => {
            requestAnimationFrame(() => {
              scheduleObj.current?.removeSelectedClass()
              reapplyAppointmentColors()
            })
          })
          return
        }

        requestAnimationFrame(() => reapplyAppointmentColors())
      },
      [
        handleInteractionEnd,
        resourceDataSource,
        handleScheduleChange,
        rejectUnavailableRange,
        reapplyAppointmentColors,
        instanceKey,
      ],
    )

    const handleCalendarDataBound = useCallback(() => {
      handleDataBound()
      markReady()
    }, [handleDataBound, markReady])

    const resourcesRef = useRef(resources)
    resourcesRef.current = resources

    const resourceHeaderTemplate = useCallback((props: ObjectType) => {
      const selectedResource = props.resourceData.resourceId
      return (
        resourcesRef.current.find(({ resourceId }) => resourceId === selectedResource)
          ?.resourceTemplate || ''
      )
    }, [])

    useEffect(() => {
      const schedule = scheduleObj.current
      if (!schedule || (schedule as unknown as { isDestroyed?: boolean }).isDestroyed) {
        return
      }

      try {
        schedule.refreshTemplates('resourceHeaderTemplate')
      } catch {
        return
      }

      const frameId = requestAnimationFrame(() => applyDynamicColumnWidths())
      return () => cancelAnimationFrame(frameId)
    }, [resourceDataSource, applyDynamicColumnWidths, focusedStaffIds])

    const indentTemplate = useCallback(() => {
      const isToday =
        !!day &&
        !!timeZone &&
        DateService.preserveDateInTimezone(day, timeZone).isSame(
          DateService.getCurrentTimeInTimezone(timeZone),
          'day',
        )
      return (
        <div className='center'>
          {showAnnounceButton && isToday && <AnnounceIconButton onClick={toggleAnnounceModal} />}
        </div>
      )
      // Deps must match what's used (day/timeZone/toggle) — NOT `rooms`. Depending on `rooms` made
      // this template's identity churn on every refetch, triggering a full schedule layout
      // rebuild (headerIndentTemplate change → isLayout) on drag/drop and modal edits.
    }, [day, timeZone, toggleAnnounceModal, showAnnounceButton])

    const handleSendNotifications = useCallback(async () => {
      await sendInfoPatientsNotification(eventsToSendNotification)
      setIsSendSmsConfirmVisible(false)
    }, [eventsToSendNotification])

    const handleToggleSendSmsConfirmVisible = useCallback(
      (event: boolean) => () => {
        setIsSendSmsConfirmVisible(event)
      },
      [],
    )

    const handleToggleSendBusyNotification = useCallback(
      (event: boolean) => () => {
        setShowBusyNotification(event)
      },
      [],
    )

    const paintAvailabilityCell = useCallback(
      (element: HTMLElement, cellStart: Date, resourceId: string) => {
        element.classList.remove(
          'schedule-availability-in',
          'schedule-availability-out',
          'schedule-availability-lunch',
          'schedule-availability-none',
        )
        delete element.dataset.availabilityLabel
        if (element.classList.contains('e-disabled-cell')) return

        const blocks = availabilityBlocksByResource.get(resourceId) ?? []
        const status = statusForCell(blocks, cellStart)
        if (!status) return

        element.classList.add(availabilityClassName(status))
        const label = availabilityLabelForCell(blocks, cellStart, interval)
        if (label) element.dataset.availabilityLabel = label
      },
      [availabilityBlocksByResource, interval],
    )

    const handleRenderCell = useCallback(
      (args: RenderCellEventArgs) => {
        if (args.elementType !== 'workCells') {
          return
        }

        const cellStartTime = new Date(args.date)
        const resource = resources[args.groupIndex]
        if (args.element instanceof HTMLElement) {
          paintAvailabilityCell(args.element, cellStartTime, String(resource?.resourceId ?? ''))
        }
      },
      [resources, rooms, resourceGrouping, paintAvailabilityCell],
    )

    useEffect(() => {
      const root = scheduleContainerRef.current
      if (!root) return
      root.querySelectorAll<HTMLTableCellElement>('td.e-work-cells').forEach((cell) => {
        const timestamp = Number(cell.getAttribute('data-date'))
        const groupIndex = Number(cell.getAttribute('data-group-index'))
        if (!Number.isFinite(timestamp)) return
        const resourceId = String(resources[groupIndex]?.resourceId ?? '')
        paintAvailabilityCell(cell, new Date(timestamp), resourceId)
      })
    }, [paintAvailabilityCell, resources, isReady])

    const group = useMemo(() => ({ resources: ['Rooms'], enableCompactView: false }), [])

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          scheduleContainerRef.current &&
          !scheduleContainerRef.current.contains(event.target as Node)
        ) {
          handleRemoveSelectedCells()
        }
      }

      document.addEventListener('click', handleClickOutside)

      return () => {
        document.removeEventListener('click', handleClickOutside)
      }
    }, [handleRemoveSelectedCells])

    const scheduleHeight = useScheduleHeight(stacked)

    return (
      <div className='schedule-control-section' ref={scheduleContainerRef}>
        {!isReady && (
          <div className='schedule-skeleton-overlay'>
            <PatientScheduleSkeleton />
          </div>
        )}
        <div className={isReady ? undefined : 'schedule-pending'}>
          <div className='col-lg-12 control-section'>
            <div className={`control-wrapper ${!timeZone ? 'loading' : ''}`}>
              {resources.length > 0 && timeZone && (
                <ScheduleComponent
                  key={instanceKey ?? day}
                  ref={scheduleObj}
                  cssClass='room-schedule'
                  width='100%'
                  height={scheduleHeight}
                  timezone={timeZone}
                  timeScale={timeScale}
                  selectedDate={selectedDate}
                  startHour={dayInterval.min as string}
                  endHour={dayInterval.max as string}
                  workHours={workHours}
                  currentView='Day'
                  headerIndentTemplate={indentTemplate}
                  resourceHeaderTemplate={resourceHeaderTemplate}
                  eventRendered={eventRendered}
                  showQuickInfo={false}
                  eventClick={handleEventClick}
                  actionBegin={handleChange}
                  dataBound={handleCalendarDataBound}
                  actionFailure={handleActionFailure}
                  showHeaderBar={false}
                  select={handleSelect}
                  renderCell={handleRenderCell}
                  popupOpen={handlePopupOpen}
                  allowDragAndDrop
                  dragStart={handleInteractionStart}
                  dragStop={handleDragStop}
                  resizeStart={handleInteractionStart}
                  resizeStop={handleInteractionEnd}
                  eventSettings={eventSettings}
                  group={group}>
                  <ResourcesDirective>
                    <ResourceDirective
                      field='room_id'
                      title='Rooms'
                      name='Rooms'
                      dataSource={resourceDataSource}
                      idField='resourceId'
                    />
                  </ResourcesDirective>
                  <ViewsDirective>
                    <ViewDirective option='Day' eventTemplate={eventTemplate} />
                  </ViewsDirective>
                  <Inject services={[Day, Resize, DragAndDrop]} />
                </ScheduleComponent>
              )}

              <Show
                when={
                  showNotifyPatients &&
                  !DateService.isPast(new Date(`${formattedDate} 23:59`), timeZone) &&
                  eventsToSendNotification.length > 0
                }>
                <div className='action'>
                  <Button
                    modifier={BUTTON_MODIFIER.QUATERNARY}
                    onClick={handleToggleSendSmsConfirmVisible(true)}
                    loading={isSendingNotifications}>
                    <span className='center' style={{ gap: 8 }}>
                      {!isSendingNotifications && (
                        <img
                          className='actionIcon'
                          src={sendMessageIcon}
                          alt={ALT_CONSTANTS.ACTION_ICON}
                        />
                      )}
                      <span>Notify patients</span>
                    </span>
                  </Button>
                </div>
              </Show>
            </div>
          </div>

          <Warning
            open={Boolean(pendingCreation)}
            title='Doctor is away'
            warning='The doctor is away or outside available hours during this appointment. Do you still want to create it?'
            okButtonText='Continue'
            handleClose={cancelCreation}
            handleConfirm={confirmCreation}
            hasCancel
          />

          <Warning
            open={Boolean(awaitConfirmationEvent)}
            title={INFO_CONSTANTS.ARE_YOU_SURE}
            warning={INFO_CONSTANTS.MULTIPLE_PATIENTS_TITLE}
            okButtonText={INFO_CONSTANTS.YES}
            handleClose={() => setAwaitConfirmationEvent(null)}
            handleConfirm={proceedToUpdateEvents}
            hasCancel
          />

          <Warning
            open={showBusyNotification}
            title={INFO_CONSTANTS.INFO}
            warning={INFO_CONSTANTS.BUSY_ROOM_WARNING}
            handleClose={handleToggleSendBusyNotification(false)}
            okButtonText={BUTTON_CONSTANTS.CLOSE}
            handleConfirm={handleToggleSendBusyNotification(false)}
          />

          <SendSMSConfirmation
            open={isSendSmsConfirmVisible}
            handleConfirm={handleSendNotifications}
            handleClose={handleToggleSendSmsConfirmVisible(false)}
          />
          <AnnounceModal
            open={openAnnounceModal}
            toggleOpen={toggleAnnounceModal}
            rooms={rooms.map((room) => room.id).filter((id) => !!id)}
          />
        </div>
      </div>
    )
  },
)
